import { isDevMode } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ToastService } from '../services/toast.service';
import { UserService } from '../services/user.service';

let redirectInFlight = false;

const REDIRECT_DELAY_MS = 2000;

const SESSION_EXPIRED_MSG = 'Your session has expired. You will be redirected to sign in.';

export function isWorkflowBuilderUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0].replace(/\/$/, '');
  return path === '/workflow-builder' || path.endsWith('/workflow-builder');
}

/**
 * Generate Workflow / Recent Workflows UI and `/workflow-builder` — off in production builds.
 */
export function showWorkflowBuilderUi(): boolean {
  return environment.production !== true;
}

/** Browser path for HashLocationStrategy (`/#/workflow-builder`) or path routing. */
export function currentAppPath(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const hash = window.location.hash.replace(/^#/, '');
  const raw = hash || window.location.pathname || '';
  return raw.split(/[?#]/)[0].replace(/\/$/, '') || '/';
}

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

/**
 * Local development only, and only when the environment opts in: `/workflow-builder`
 * opens without a session. Uses `isDevMode()` or an explicit localhost host so
 * `http://localhost:9875/#/workflow-builder` works without forcing ATS redirect.
 */
export function allowsSessionlessWorkflowBuilder(url?: string): boolean {
  if (environment.allowWorkflowBuilderWithoutSession !== true) {
    return false;
  }
  if (!isDevMode() && !isLocalDevHost()) {
    return false;
  }
  return isWorkflowBuilderUrl(url ?? currentAppPath());
}

/**
 * Toast + delayed redirect to ATS (same behavior as the unauthorized HTTP interceptor).
 * Does **not** call ATS `/users/logout` — invalid/expired session is cleared client-side only.
 * Use from code paths that bypass Angular interceptors (e.g. `fetch` + SSE).
 * Duplicate calls are ignored until navigation completes.
 * Skipped on local `/workflow-builder` when sessionless access is enabled (no forced logout).
 */
export function redirectToAtsAfterUnauthorized(user: UserService, toast?: ToastService): void {
  if (redirectInFlight || typeof window === 'undefined') {
    return;
  }
  if (allowsSessionlessWorkflowBuilder()) {
    return;
  }
  redirectInFlight = true;
  if (toast) {
    toast.warn(SESSION_EXPIRED_MSG);
    window.setTimeout(() => {
      user.redirectToAtsAfterInvalidSession(environment.defaultATSHost);
    }, REDIRECT_DELAY_MS);
  } else {
    user.redirectToAtsAfterInvalidSession(environment.defaultATSHost);
  }
}
